import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { ArrowLeft, Save, Upload, Eye, EyeOff, FileText } from 'lucide-react';

const AdminCMSWeeklyMenu = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const autoSaveTimer = useRef(null);
  const originalPublished = useRef(false);
  
  const [form, setForm] = useState({
    title: 'Menu de la semaine',
    subtitle: 'Du mardi au vendredi midi',
    menu_price: '25€',
    validity_start: '',
    validity_end: '',
    starter_title: 'Entrée',
    starter_name: '',
    starter_description: '',
    main_title: 'Plat',
    main_name: '',
    main_description: '',
    dessert_title: 'Dessert',
    dessert_name: '',
    dessert_description: '',
    note: 'Ce menu évolue chaque semaine selon les produits de saison.',
    pdf_url: null,
    pdf_filename: null,
    show_pdf: true,
    is_published: false,
    show_on_website: true
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (hasChanges) {
      autoSaveTimer.current = setTimeout(() => {
        handleAutoSave();
      }, 30000);
    }
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [form, hasChanges]);

  const fetchMenu = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/weekly-menu`, { headers });
      if (!response.ok) throw new Error('Unauthorized');
      const data = await response.json();
      if (data) {
        setForm(prev => ({ ...prev, ...data }));
        originalPublished.current = data.is_published || false;
      }
    } catch (error) {
      console.log('No existing menu, starting fresh');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/weekly-menu`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, is_published: originalPublished.current })
      });
      setHasChanges(false);
      toast({ title: "Brouillon sauvegardé", description: "Auto-save", duration: 2000 });
    } catch (error) {
      console.error('Auto-save failed');
    }
  };

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSaving(true);
    try {
      // Check if menu exists
      const checkRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/weekly-menu`, { headers });
      const existing = await checkRes.json();
      
      const method = existing ? 'PUT' : 'POST';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/weekly-menu`, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        setHasChanges(false);
        originalPublished.current = form.is_published;
        toast({ title: "Succès", description: "Menu de la semaine enregistré" });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = (newValue) => {
    // If trying to unpublish and currently published, show confirmation
    if (!newValue && originalPublished.current) {
      setShowUnpublishDialog(true);
    } else {
      updateField('is_published', newValue);
    }
  };

  const confirmUnpublish = () => {
    updateField('is_published', false);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const headers = getAuthHeader();
    if (!headers) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/weekly-menu/pdf`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Extract filename from the file object
        const filename = file.name;
        setForm(prev => ({ ...prev, pdf_url: data.pdf_url, pdf_filename: filename }));
        toast({ title: "Succès", description: `PDF "${filename}" téléchargé` });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Échec du téléchargement", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Unpublish Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showUnpublishDialog}
        onClose={() => setShowUnpublishDialog(false)}
        onConfirm={confirmUnpublish}
        title="Dépublier ce menu ?"
        message="Ce menu ne sera plus visible sur le site. Les visiteurs verront le menu par défaut."
        confirmText="Dépublier"
        cancelText="Annuler"
        variant="danger"
      />

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/cms" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Menu de la Semaine</h1>
              <p className="text-sm text-gray-500">Ce menu change chaque semaine</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 hidden md:inline">Modifications non sauvegardées</span>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              <Save size={18} />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Statut du menu</h2>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.is_published}
                onCheckedChange={handlePublishToggle}
                data-testid="weekly-menu-publish-toggle"
              />
              <div>
                <Label className="font-medium">
                  {form.is_published ? 'Publié' : 'Brouillon'}
                </Label>
                <p className="text-xs text-gray-500">
                  {form.is_published ? 'Visible sur le site' : 'Non visible'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.show_on_website}
                onCheckedChange={(v) => updateField('show_on_website', v)}
              />
              <div>
                <Label className="font-medium">Afficher sur le site</Label>
                <p className="text-xs text-gray-500">Section visible dans "Le Midi"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Titre du menu</Label>
              <Input
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="mt-1"
                data-testid="weekly-menu-title"
              />
            </div>
            <div>
              <Label>Prix du menu</Label>
              <Input
                value={form.menu_price}
                onChange={(e) => updateField('menu_price', e.target.value)}
                placeholder="25€"
                className="mt-1"
                data-testid="weekly-menu-price"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Sous-titre / Période de validité</Label>
              <Input
                value={form.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="Du mardi au vendredi midi"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Date de début</Label>
              <Input
                type="date"
                value={form.validity_start || ''}
                onChange={(e) => updateField('validity_start', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Date de fin</Label>
              <Input
                type="date"
                value={form.validity_end || ''}
                onChange={(e) => updateField('validity_end', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Starter */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Entrée</h2>
          <div className="space-y-4">
            <div>
              <Label>Nom du plat</Label>
              <Input
                value={form.starter_name}
                onChange={(e) => updateField('starter_name', e.target.value)}
                placeholder="Ex: Rémoulade de carottes, céleri-rave et pomme granny"
                className="mt-1"
                data-testid="weekly-menu-starter-name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.starter_description}
                onChange={(e) => updateField('starter_description', e.target.value)}
                placeholder="Ex: Graines de moutarde, herbes fraîches et magret fumé"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Main Course */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Plat</h2>
          <div className="space-y-4">
            <div>
              <Label>Nom du plat</Label>
              <Input
                value={form.main_name}
                onChange={(e) => updateField('main_name', e.target.value)}
                placeholder="Ex: Poulet sauté façon chasseur, jus corsé"
                className="mt-1"
                data-testid="weekly-menu-main-name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.main_description}
                onChange={(e) => updateField('main_description', e.target.value)}
                placeholder="Ex: Mousseline de panais et champignons de Paris"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Dessert */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Dessert</h2>
          <div className="space-y-4">
            <div>
              <Label>Nom du dessert</Label>
              <Input
                value={form.dessert_name}
                onChange={(e) => updateField('dessert_name', e.target.value)}
                placeholder="Ex: Baba bouchon légèrement imbibé au rhum"
                className="mt-1"
                data-testid="weekly-menu-dessert-name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.dessert_description}
                onChange={(e) => updateField('dessert_description', e.target.value)}
                placeholder="Ex: Chantilly vanille onctueuse"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Note (optionnel)</h2>
          <Textarea
            value={form.note}
            onChange={(e) => updateField('note', e.target.value)}
            placeholder="Ex: Ce menu évolue chaque semaine selon les produits de saison."
            rows={2}
          />
        </div>

        {/* PDF Upload */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Document PDF</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.show_pdf}
                onCheckedChange={(v) => updateField('show_pdf', v)}
              />
              <Label>Afficher le lien PDF sur le site</Label>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  <Upload size={18} />
                  <span>{uploading ? 'Téléchargement...' : (form.pdf_url ? 'Remplacer le PDF' : 'Télécharger un PDF')}</span>
                </div>
              </label>
              
              {form.pdf_url && (
                <div className="flex items-center gap-4">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${form.pdf_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    <FileText size={18} />
                    {form.pdf_filename || 'Voir le PDF'}
                  </a>
                </div>
              )}
            </div>
            
            {form.pdf_url && (
              <p className="text-xs text-gray-500">
                Pour remplacer le PDF, téléchargez simplement un nouveau fichier.
              </p>
            )}
          </div>
        </div>

        {/* Auto-save info */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Auto-save :</strong> Vos modifications sont sauvegardées automatiquement en brouillon toutes les 30 secondes.
          </p>
        </div>

        {/* Save Button (mobile) */}
        <div className="md:hidden">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminCMSWeeklyMenu;
